import { useState, useRef, useEffect, useMemo } from 'react'
import { useLocation, Link } from 'react-router-dom'
import { HelpCircle, X, BookOpen, Clock, FileText, PlayCircle, Map, ListOrdered, HelpCircle as HelpIcon, ScrollText, ChevronRight } from 'lucide-react'
import { tutorials } from '../data/catalog'
import { useTutorialEvents } from '../hooks/useTutorialEvents'
import type { Tutorial, TutorialCategory } from '../types'
import type { TutorialProgressStatus } from '../types'
import { useTutorialProgress } from '../hooks/useTutorialProgress'

const formatIconMap: Record<string, React.ReactNode> = {
  article: <FileText className="w-3.5 h-3.5" />,
  video: <PlayCircle className="w-3.5 h-3.5" />,
  'interactive-tour': <Map className="w-3.5 h-3.5" />,
  'step-by-step': <ListOrdered className="w-3.5 h-3.5" />,
  faq: <HelpIcon className="w-3.5 h-3.5" />,
  cheatsheet: <ScrollText className="w-3.5 h-3.5" />,
}

const pathnameToCategories: Record<string, TutorialCategory[]> = {
  '/': ['getting-started', 'tips'],
  '/appointments': ['agenda'],
  '/waitlist': ['agenda'],
  '/patients': ['patients'],
  '/treatments': ['treatments'],
  '/consents': ['consents'],
  '/lab': ['lab'],
  '/billing': ['billing'],
  '/budgets': ['budgets'],
  '/insurance': ['insurance'],
  '/inventory': ['inventory'],
  '/reports': ['reports'],
  '/clinical-kpi': ['reports'],
  '/clinical-kpi/doctors': ['reports'],
  '/clinical-kpi/treatments': ['reports', 'treatments'],
  '/reception/checkin': ['reception'],
  '/marketing': ['marketing'],
  '/users': ['admin'],
  '/permissions': ['admin'],
  '/clinics': ['admin'],
  '/audit': ['admin'],
}

function getRelevantCategories(pathname: string): TutorialCategory[] {
  // Exact match
  if (pathnameToCategories[pathname]) {
    return pathnameToCategories[pathname]
  }
  // Prefix match (e.g. /patients/123 -> /patients)
  for (const [path, cats] of Object.entries(pathnameToCategories)) {
    if (pathname.startsWith(path + '/')) {
      return cats
    }
  }
  return ['tips']
}

function getRelevantTutorials(
  pathname: string,
  getProgress: (id: string) => TutorialProgressStatus
): Tutorial[] {
  const categories = getRelevantCategories(pathname)
  const relevant = tutorials
    .filter((t) => categories.includes(t.category))
    .sort((a, b) => {
      // Prioritize not started, then in_progress, then completed
      const statusOrder: Record<TutorialProgressStatus, number> = {
        'not_started': 0,
        'in_progress': 1,
        'completed': 2,
      }
      const statusA = getProgress(a.id)
      const statusB = getProgress(b.id)
      if (statusOrder[statusA] !== statusOrder[statusB]) {
        return statusOrder[statusA] - statusOrder[statusB]
      }
      return a.sortOrder - b.sortOrder
    })
  return relevant.slice(0, 3)
}

export function ContextualHelpButton() {
  const location = useLocation()
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const { getProgress } = useTutorialProgress()
  const { trackHelpClicked } = useTutorialEvents()

  const relevantTutorials = useMemo(() => {
    if (location.pathname.startsWith('/tutorials')) return []
    return getRelevantTutorials(location.pathname, getProgress)
  }, [location.pathname, getProgress])

  const hasUnread = relevantTutorials.some((t) => getProgress(t.id) === 'not_started')

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  // Close on route change
  useEffect(() => {
    setOpen(false)
  }, [location.pathname])

  if (relevantTutorials.length === 0) return null

  return (
    <div ref={containerRef} className="fixed bottom-24 right-5 z-50">
      {/* Dropdown */}
      {open && (
        <div className="absolute bottom-14 right-0 w-80 bg-surface-1 border border-border rounded-xl shadow-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-border-subtle flex items-center justify-between">
            <div className="flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-primary-600" />
              <span className="text-sm font-semibold text-text-primary">Tutoriales recomendados</span>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="p-1 rounded hover:bg-surface-2 text-text-tertiary"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="py-1">
            {relevantTutorials.map((t) => {
              const status = getProgress(t.id)
              return (
                <Link
                  key={t.id}
                  to={`/tutorials/${t.slug}`}
                  className="flex items-start gap-3 px-4 py-3 hover:bg-surface-2 transition-colors border-b border-border-subtle last:border-0"
                >
                  <div className="mt-0.5 text-text-tertiary flex-shrink-0">
                    {formatIconMap[t.format]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-text-primary truncate">
                      {t.title}
                    </p>
                    <p className="text-xs text-text-tertiary mt-0.5 flex items-center gap-2">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {t.duration} min
                      </span>
                      {status === 'completed' && (
                        <span className="text-emerald-600">Completado</span>
                      )}
                      {status === 'in_progress' && (
                        <span className="text-primary-600">En progreso</span>
                      )}
                    </p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-text-tertiary flex-shrink-0 mt-0.5" />
                </Link>
              )
            })}
          </div>
          <div className="px-4 py-2 border-t border-border-subtle bg-surface-0">
            <Link
              to="/tutorials"
              onClick={() => setOpen(false)}
              className="text-xs text-primary-600 hover:text-primary-700 font-medium"
            >
              Ver todos los tutoriales →
            </Link>
          </div>
        </div>
      )}

      {/* Floating button */}
      <button
        onClick={() => {
          if (!open) trackHelpClicked(location.pathname)
          setOpen((v) => !v)
        }}
        className={`relative w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-all ${
          open
            ? 'bg-primary-700 text-white'
            : 'bg-primary-600 text-white hover:bg-primary-700'
        }`}
        aria-label="Ayuda contextual"
      >
        {open ? (
          <X className="w-5 h-5" />
        ) : (
          <HelpCircle className="w-5 h-5" />
        )}
        {hasUnread && !open && (
          <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-error-500 rounded-full border-2 border-white" />
        )}
      </button>
    </div>
  )
}
